import type {
  MultiStoreCenter,
  MultiStoreCenterDay,
  MultiStoreCenterResult,
  MultiStoreNearHour,
  MultiStorePointOfSale,
} from '../../interface/multi-store';
import type { PointOfSale } from '../../interface/point-of-sale';
import { findSurroundingHours } from './hours';

// Centri inclusi nell'ordine origin + aggiunti. L'origin (centro scelto nello step "dove")
// ha distanza 0; gli aggiunti prendono anagrafica e distanza dai candidati.
// Il centro corrente resta sempre incluso con l'anagrafica dello store, anche se l'elenco
// dei candidati non lo contiene (in caricamento, in errore o cambiato nel frattempo).
// Gli altri id senza un candidato corrispondente si scartano.
export const resolveIncludedCenters = (
  includedIds: string[],
  origin: PointOfSale,
  current: PointOfSale,
  candidates: MultiStorePointOfSale[]
): MultiStoreCenter[] =>
  includedIds.flatMap((id): MultiStoreCenter[] => {
    if (id === origin.id) {
      return [
        {
          pointOfSale: origin,
          isOrigin: true,
          isCurrent: id === current.id,
          distance: 0,
        },
      ];
    }
    const candidate = candidates.find((pointOfSale) => pointOfSale.id === id);
    if (id === current.id) {
      return [
        {
          pointOfSale: current,
          isOrigin: false,
          isCurrent: true,
          distance: candidate?.distance,
        },
      ];
    }
    if (candidate === undefined) return [];
    return [
      {
        pointOfSale: candidate,
        isOrigin: false,
        isCurrent: false,
        distance: candidate.distance,
      },
    ];
  });

type CenterQueryState = Pick<
  MultiStoreCenterResult<unknown>,
  'isPending' | 'isFetching' | 'isUnavailable'
>;

// Centri alternativi la cui richiesta è in corso (results nello stesso ordine dei centri):
// finché rispondono gli orari mostrano il segnaposto invece di "nessun orario". Una query
// disabilitata (giorno non ancora scelto) è pending ma non in corso, quindi non si segna.
export const getLoadingCenterIds = (
  centers: MultiStoreCenter[],
  results: CenterQueryState[]
): string[] =>
  centers
    .filter(
      (_, index) =>
        results[index]?.isPending === true &&
        results[index]?.isFetching === true
    )
    .map((center) => center.pointOfSale.id);

// Centri alternativi in errore o omessi dal backend: restano fuori dall'unione
export const getUnavailableCenterIds = (
  centers: MultiStoreCenter[],
  results: CenterQueryState[]
): string[] =>
  centers
    .filter((_, index) => results[index]?.isUnavailable === true)
    .map((center) => center.pointOfSale.id);

// Nessuna query ancora in attesa di una risposta
export const isAllSettled = (
  results: Array<{ isPending: boolean }>
): boolean => results.every((result) => !result.isPending);

const compareDistance = (a?: number, b?: number): number => {
  if (a === undefined) return b === undefined ? 0 : 1;
  if (b === undefined) return -1;
  return a - b;
};

// Distanza crescente con le distanze mancanti in fondo; a parità resta l'ordine di partenza
export const sortByDistance = <T extends { distance?: number }>(
  items: T[]
): T[] => [...items].sort((a, b) => compareDistance(a.distance, b.distance));

// Centro più vicino che ha l'orario libero: è quello verso cui si cambia al clic sull'orario
export const findClosestCenterWithHour = (
  centers: MultiStoreCenterDay[],
  hour: string
): MultiStoreCenterDay | undefined =>
  sortByDistance(centers.filter((center) => center.times.includes(hour)))[0];

// Centri liberi all'orario scelto: il corrente per primo, poi per distanza
export const getCentersWithHour = (
  centers: MultiStoreCenterDay[],
  hour: string
): MultiStoreCenterDay[] => {
  const withHour = sortByDistance(
    centers.filter((center) => center.times.includes(hour))
  );
  return [
    ...withHour.filter((center) => center.isCurrent),
    ...withHour.filter((center) => !center.isCurrent),
  ];
};

// Centro non libero all'orario scelto: gli orari liberi più vicini nello stesso giorno, il
// precedente e il successivo, in ordine. Nessuno se quel giorno il centro non ha orari.
export const getNearHours = (
  center: MultiStoreCenterDay,
  hour: string
): MultiStoreNearHour[] => {
  const { before, after } = findSurroundingHours(center.times, hour);
  return [before, after].flatMap((nearHour): MultiStoreNearHour[] =>
    nearHour === undefined ? [] : [{ center, hour: nearHour }]
  );
};
